import { useCallback, useMemo, useState } from 'react';
import Cropper, { type MediaSize } from 'react-easy-crop';

import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import { modal } from '@eduzz/ui-antd-theme';

import { type CroppedArea, useCroppedImage } from '@/hooks/useCroppedImage';
import { useFileSelector } from '@/hooks/useFileSelector';

import { COMMON_IMAGE_MIME_TYPES } from './globals/MimeTypes';
import { Modrawer } from './Modrawer';

export const ImageCropper = ({
  open,
  onCrop,
  size
}: {
  open: boolean;
  onCrop: (image: string | null) => void;
  size: number;
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [croppedArea, setCroppedArea] = useState<CroppedArea | null>(null);
  const [minZoom, setMinZoom] = useState(1);

  const { selectImage } = useFileSelector();
  const { cropImage } = useCroppedImage();

  const selectImageHandler = async () => {
    try {
      const [file] = await selectImage();

      if (!COMMON_IMAGE_MIME_TYPES.has(file.type)) {
        modal.error({
          title: 'Formato inválido',
          content: 'O arquivo selecionado não é uma imagem válida.'
        });
        return;
      }

      setFile(file);
    } catch (error) {
      setFile(null);
    }
  };

  const image = useMemo(() => {
    if (!file) {
      return '';
    }

    return URL.createObjectURL(file);
  }, [file]);

  const onCropComplete = useCallback(async (_: CroppedArea, croppedAreaPixels: CroppedArea) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const save = useCallback(async () => {
    if (!croppedArea) {
      onCrop(null);
      return;
    }

    try {
      const result = await cropImage(image, croppedArea);
      onCrop(result);
    } catch (err) {
      modal.error({
        title: 'Erro ao cortar a imagem',
        content: 'Ocorreu um erro ao cortar a imagem. Por favor, tente novamente.'
      });
      onCrop(null);
    }
  }, [croppedArea, cropImage, image, onCrop]);

  const calcMinZoom = (mediaSize: MediaSize) => {
    const renderedWidth = mediaSize.width;
    const renderedHeight = mediaSize.height;

    const minSize = Math.min(renderedWidth, renderedHeight);
    const zoom = size / minSize;

    setMinZoom(zoom);
    setZoom(zoom);
  };

  return (
    <Modrawer
      open={open}
      width={350}
      height={350}
      onCancel={() => onCrop(null)}
      onOk={() => save()}
      footer={[
        <Button
          id='crop-ok-button'
          key='ok'
          className='override-button-base! w-full md:w-auto'
          type='primary'
          onClick={() => save()}
        >
          Concluir
        </Button>
      ]}
      title={'Enviar imagem'}
    >
      {image ? (
        <div className='flex flex-col gap-2'>
          <div className='relative h-[196px] w-full overflow-hidden rounded-md bg-gray-100 dark:bg-neutral-700'>
            <Cropper
              onMediaLoaded={mediaSize => calcMinZoom(mediaSize)}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={5}
              image={image}
              aspect={size / size}
              cropSize={{ width: size, height: size }}
            />
          </div>
          <Button className='override-button-sm!' id='change-image' onClick={selectImageHandler}>
            <ReloadOutlined /> Escolher outra imagem
          </Button>
        </div>
      ) : (
        <div
          className='bg-surface-subtle border-outline-default flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed p-4 text-center'
          onClick={selectImageHandler}
          id='upload-image-button'
        >
          <div className='flex justify-center'>
            <div className='flex justify-center rounded-full bg-blue-50'>
              <UploadOutlined className='text-primary! p-6 text-4xl' />
            </div>
          </div>
          <div className='text-primary font-bold'>Toque para enviar uma imagem</div>
          <div>JPG, JPEG ou PNG</div>
          <div className='text-neutral-400'>
            Recomendamos utilizar uma imagem quadrada (largura: {size}px, altura: {size}px)
          </div>
        </div>
      )}
    </Modrawer>
  );
};
